import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Modal, Form, Input, Select, InputNumber, Spin, Alert, Tooltip } from 'antd';
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCoinNetworks, useCreateCoinNetwork, useUpdateCoinNetwork, useDeleteCoinNetwork, useCheckCoinNetwork } from '../hooks/useCoinNetworkManagement';
import { useCoins } from '../hooks/useCoinManagement';
import { useNetworks } from '../hooks/useNetworkManagement';
import { getUserIdFromToken } from '../utils/authUtils';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../utils/notificationUtils';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;
const { Option } = Select;

const CoinNetworksManagementPage: React.FC = () => {
  const { t } = useTranslation('coinNetworks');
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedCoinNetwork, setSelectedCoinNetwork] = useState<any>(null);
  const [form] = Form.useForm();
  const [selectedCoin, setSelectedCoin] = useState<string>('');
  const [selectedNetwork, setSelectedNetwork] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [filterCoin, setFilterCoin] = useState<string>('');
  const [filterNetwork, setFilterNetwork] = useState<string>('');
  const [filteredCoinNetworks, setFilteredCoinNetworks] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Fetch coin networks data
  const {
    data: coinNetworksData,
    isLoading: isLoadingCoinNetworks,
    isError: isCoinNetworksError,
    refetch: refetchCoinNetworks
  } = useCoinNetworks();

  // Fetch coins data for selection
  const {
    data: coinsData,
    isLoading: isLoadingCoins,
    isError: isCoinsError
  } = useCoins();

  // Fetch networks data for selection
  const {
    data: networksData,
    isLoading: isLoadingNetworks,
    isError: isNetworksError
  } = useNetworks();

  // Log network data for debugging
  useEffect(() => {
    if (networksData) {
      console.log('Networks data:', networksData);
    }
  }, [networksData]);

  // Check if coin network exists when both coin and network are selected
  const {
    data: existingCoinNetwork,
    isLoading: isCheckingCoinNetwork
  } = useCheckCoinNetwork(selectedCoin, selectedNetwork);

  // Mutations for CRUD operations
  const createCoinNetworkMutation = useCreateCoinNetwork();
  const updateCoinNetworkMutation = useUpdateCoinNetwork();
  const deleteCoinNetworkMutation = useDeleteCoinNetwork();

  // Format coin networks data for table using useMemo to avoid re-creating on every render
  const coinNetworks = useMemo(() => {
    return coinNetworksData?.map((coinNetwork: any) => ({
      key: coinNetwork._id || coinNetwork.id,
      id: coinNetwork._id || coinNetwork.id,
      // Use the correct fields from the API response
      coin: coinNetwork.coin_symbol || coinNetwork.coin,
      coin_full_name: coinNetwork.coin_full_name || '',
      network: coinNetwork.network_title || coinNetwork.network,
      network_full_name: coinNetwork.network_full_name || '',
      contract_address: coinNetwork.contract_address || '',
      decimal: coinNetwork.decimal,
      min_transaction: coinNetwork.min_transaction,
      max_transaction: coinNetwork.max_transaction,
      min_base_coin: coinNetwork.min_base_coin,
      deposit_fee: coinNetwork.deposit_fee,
      fee: coinNetwork.fee,
      confirm_count: coinNetwork.confirm_count || 0,
      same_address: coinNetwork.same_address || 'Yes',
      status: coinNetwork.status
    })) || [];
  }, [coinNetworksData]);

  // Filter coin networks based on search text and dropdown filters
  useEffect(() => {
    let filtered = [...coinNetworks];

    // Apply search text filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((coinNetwork: any) => (
        (coinNetwork.coin && coinNetwork.coin.toLowerCase().includes(searchLower)) ||
        (coinNetwork.network && coinNetwork.network.toLowerCase().includes(searchLower)) ||
        (coinNetwork.contract_address && coinNetwork.contract_address.toLowerCase().includes(searchLower))
      ));
    }

    // Apply coin filter
    if (filterCoin) {
      filtered = filtered.filter((coinNetwork: any) =>
        coinNetwork.coin === filterCoin
      );
    }

    // Apply network filter
    if (filterNetwork) {
      filtered = filtered.filter((coinNetwork: any) =>
        coinNetwork.network === filterNetwork
      );
    }

    setFilteredCoinNetworks(filtered);

    // Reset to first page when filters change
    setCurrentPage(1);
  }, [coinNetworks, searchText, filterCoin, filterNetwork]);

  // Format coins data for selection - match legacy dashboard format
  const coins = useMemo(() => {
    return coinsData?.map((coin: any) => ({
      id: coin._id || coin.id,
      symbol: coin.symbol || '',
      full_name: coin.full_name || ''
    })) || [];
  }, [coinsData]);

  // Format networks data for selection - match legacy dashboard format
  // Handle different response formats from the API
  const networks = useMemo(() => {
    console.log('Raw networksData:', networksData);

    if (!networksData) return [];

    // Handle array format directly
    if (Array.isArray(networksData)) {
      return networksData.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // Handle object with data property (standard API response)
    if (networksData.data && Array.isArray(networksData.data)) {
      return networksData.data.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // Handle object with networks property (some API responses)
    if (networksData.networks && Array.isArray(networksData.networks)) {
      return networksData.networks.map((network: any) => ({
        id: network._id || network.id,
        title: network.title || '',
        full_name: network.full_name || ''
      }));
    }

    // If we can't determine the format, return empty array
    console.error('Unknown network data format:', networksData);
    return [];
  }, [networksData]);

  // Log formatted data for debugging
  useEffect(() => {
    if (coins.length > 0) {
      console.log('Formatted coins for selection:', coins);
    }
    if (networks.length > 0) {
      console.log('Formatted networks for selection:', networks);
    }
  }, [coins, networks]);

  // Table columns
  const columns = [
    {
      title: t('coin'),
      dataIndex: 'coin',
      key: 'coin',
      render: (text: string, record: any) => (
        <Tooltip title={record.coin_full_name}>
          <Tag color="blue">{text}</Tag>
        </Tooltip>
      )
    },
    {
      title: t('network'),
      dataIndex: 'network',
      key: 'network',
      render: (text: string, record: any) => (
        <Tooltip title={record.network_full_name}>
          <Tag color="purple">{text}</Tag>
        </Tooltip>
      )
    },
    {
      title: t('decimal'),
      dataIndex: 'decimal',
      key: 'decimal',
    },
    {
      title: t('minTransaction'),
      dataIndex: 'min_transaction',
      key: 'min_transaction',
    },
    {
      title: t('maxTransaction'),
      dataIndex: 'max_transaction',
      key: 'max_transaction',
    },
    {
      title: t('minBaseCoin'),
      dataIndex: 'min_base_coin',
      key: 'min_base_coin',
    },
    {
      title: t('confirmCount', 'Confirm Count'),
      dataIndex: 'confirm_count',
      key: 'confirm_count',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'Active' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      )
    }
  ];

  // Handle adding a new coin network
  const handleAdd = () => {
    setIsAddModalVisible(true);
    form.resetFields();

    // Set default values for the form fields
    form.setFieldsValue({
      same_address: 'Yes',
      status: 'Active',
      decimal: 18,
      confirm_count: 12,
      min_transaction: '0.001',
      max_transaction: '1000',
      min_base_coin: '0.0001',
      deposit_fee: '0.0001',
      fee: '0.0001'
    });

    setSelectedCoin('');
    setSelectedNetwork('');

    console.log('Add coin network form initialized with default values');
  };

  // Handle editing a coin network
  const handleEdit = (coinNetwork: any) => {
    setSelectedCoinNetwork(coinNetwork);
    setIsEditModalVisible(true);
    form.setFieldsValue({
      coin: coinNetwork.coin_symbol || coinNetwork.coin,
      network: coinNetwork.network_title || coinNetwork.network,
      contract_address: coinNetwork.contract_address,
      decimal: coinNetwork.decimal,
      min_transaction: coinNetwork.min_transaction,
      max_transaction: coinNetwork.max_transaction,
      min_base_coin: coinNetwork.min_base_coin,
      deposit_fee: coinNetwork.deposit_fee,
      fee: coinNetwork.fee,
      confirm_count: coinNetwork.confirm_count || 0,
      same_address: coinNetwork.same_address || 'Yes',
      status: coinNetwork.status
    });
  };

  // Handle deleting a coin network
  const handleDelete = (coinNetwork: any) => {
    Modal.confirm({
      title: t('coinNetwork.confirmDelete'),
      content: t('coinNetwork.deleteWarning', { coin: coinNetwork.coin, network: coinNetwork.network }),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          const response = await deleteCoinNetworkMutation.mutateAsync(coinNetwork.id);

          // Check if the response indicates a soft delete (marked as inactive)
          if (response && response.message && response.message.includes('marked as inactive')) {
            // This is a soft delete response
            const dependencies = response.dependencies || {};
            const walletCount = dependencies.wallets || 0;

            let message = t('coinNetwork.markedInactiveMessage', 'The coin-network relationship was marked as inactive because it is referenced by other records:');

            if (walletCount > 0) {
              message += `\n- ${t('coinNetwork.usedByWallets', { count: walletCount, defaultValue: 'Used by {{count}} wallet(s)' })}`;
            }

            message += `\n\n${t('coinNetwork.removeDepMessage', 'To completely remove this relationship, you must first remove these dependencies.')}`;

            showWarningNotification(
              t('coinNetwork.markedInactive', 'Coin-network marked as inactive'),
              message
            );
          } else {
            // Regular successful deletion
            showSuccessNotification(t('coinNetwork.deleteSuccess', 'Coin network deleted successfully'));
          }

          // Reset search input if exists
          if (searchText) {
            setSearchText('');
          }
        } catch (error: any) {
          console.error('Error deleting coin network:', error);

          // Show more detailed error message if available
          if (error.response && error.response.data && error.response.data.error) {
            const errorMessage = error.response.data.error.message || '';
            showErrorNotification(
              t('coinNetwork.deleteError', 'Failed to delete coin network'),
              errorMessage
            );
          } else {
            showErrorNotification(t('coinNetwork.deleteError', 'Failed to delete coin network'));
          }
        }
      }
    });
  };

  // Handle coin selection change
  const handleCoinChange = (value: string) => {
    setSelectedCoin(value);
    form.setFieldsValue({ coin: value });
  };

  // Handle network selection change
  const handleNetworkChange = (value: string) => {
    setSelectedNetwork(value);
    form.setFieldsValue({ network: value });
  };

  // Handle form submission for adding a coin network
  const handleAddFormSubmit = async (values: any) => {
    try {
      // Check if coin network already exists
      if (existingCoinNetwork && existingCoinNetwork.length > 0) {
        showWarningNotification(t('coinNetwork.alreadyExists', 'This coin network combination already exists'));
        return;
      }

      // Get user ID from token
      const userId = getUserIdFromToken();
      console.log('User ID from token:', userId, 'type:', typeof userId);

      // Always use a default creator_id even if userId is not available
      // This ensures we never send null for creator_id
      // Force it to be a number (123) if userId is null or can't be converted to a number
      const creatorId = userId && !isNaN(Number(userId)) ? Number(userId) : 123;
      console.log('Creator ID to use:', creatorId, 'type:', typeof creatorId);

      // Log the form values for debugging
      console.log('Form values before submission:', values);

      // Find the selected coin and network to get their IDs
      const selectedCoinObj = coins.find((coin: any) => coin.symbol === values.coin);
      const selectedNetworkObj = networks.find((network: any) => network.title === values.network);

      if (!selectedCoinObj || !selectedNetworkObj) {
        showErrorNotification(
          t('coinNetwork.addError', 'Failed to add coin network'),
          t('coinNetwork.invalidSelection', 'Invalid coin or network selection')
        );
        return;
      }

      // Add creator_id to the coin network data and ensure proper data types
      const coinNetworkData = {
        // Use the correct field names expected by the API
        coin_id: selectedCoinObj.id,
        network_id: selectedNetworkObj.id,
        // Keep numeric fields as strings as expected by the API validation schema
        decimal: values.decimal ? values.decimal.toString() : '0',
        min_transaction: values.min_transaction ? values.min_transaction.toString() : '0',
        max_transaction: values.max_transaction ? values.max_transaction.toString() : '0',
        min_base_coin: values.min_base_coin ? values.min_base_coin.toString() : '0',
        deposit_fee: values.deposit_fee ? values.deposit_fee.toString() : '0',
        fee: values.fee ? values.fee.toString() : '0',
        confirm_count: values.confirm_count ? values.confirm_count.toString() : '0',
        // Ensure same_address is a string with "Yes" or "No"
        same_address: values.same_address || 'Yes',
        status: values.status || 'Active',
        // Set contract_address to empty string if not provided
        contract_address: values.contract_address || '',
        // Using a numeric value for creator_id as required by the model
        creator_id: creatorId
      };

      // Log the formatted data for debugging
      console.log('Formatted coin network data for submission:', coinNetworkData);
      console.log('Selected coin:', selectedCoinObj);
      console.log('Selected network:', selectedNetworkObj);

      // Submit the data
      try {
        // Add additional logging to help diagnose issues
        console.log('Submitting data to API:', JSON.stringify(coinNetworkData, null, 2));

        await createCoinNetworkMutation.mutateAsync(coinNetworkData);

        console.log('API call successful');
      } catch (apiError: any) {
        console.error('API Error Response:', apiError.response?.data);

        // Show detailed error message to the user
        if (apiError.response?.data?.error?.details?.errors) {
          const errorMessages = apiError.response.data.error.details.errors;
          showErrorNotification(
            t('coinNetwork.addError', 'Failed to add coin network'),
            Array.isArray(errorMessages) ? errorMessages.join('\n') : String(errorMessages)
          );
        } else if (apiError.response?.data?.error?.message) {
          showErrorNotification(
            t('coinNetwork.addError', 'Failed to add coin network'),
            apiError.response.data.error.message
          );
        }

        throw apiError;
      }

      // Show success notification
      showSuccessNotification(t('coinNetwork.addSuccess', 'Coin network added successfully'));

      // Close the modal and reset the form
      setIsAddModalVisible(false);
      form.resetFields();

      // Reset search input if exists
      if (searchText) {
        setSearchText('');
      }

      // Reset filters
      setFilterCoin('');
      setFilterNetwork('');
    } catch (error: any) {
      console.error('Error adding coin network:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        showErrorNotification(
          t('coinNetwork.addError', 'Failed to add coin network'),
          errorMessage
        );
      } else {
        showErrorNotification(t('coinNetwork.addError', 'Failed to add coin network'));
      }
    }
  };

  // Handle form submission for editing a coin network
  const handleEditFormSubmit = async (values: any) => {
    try {
      // Get user ID from token
      const userId = getUserIdFromToken();
      console.log('User ID from token (edit):', userId, 'type:', typeof userId);

      // Always use a default creator_id even if userId is not available
      // This ensures we never send null for creator_id
      // Force it to be a number (123) if userId is null or can't be converted to a number
      const creatorId = userId && !isNaN(Number(userId)) ? Number(userId) : 123;
      console.log('Creator ID to use (edit):', creatorId, 'type:', typeof creatorId);

      // Log the form values for debugging
      console.log('Edit form values before submission:', values);

      // Find the selected coin and network to get their IDs
      const selectedCoinObj = coins.find((coin: any) => coin.symbol === values.coin);
      const selectedNetworkObj = networks.find((network: any) => network.title === values.network);

      if (!selectedCoinObj || !selectedNetworkObj) {
        showErrorNotification(
          t('coinNetwork.updateError', 'Failed to update coin network'),
          t('coinNetwork.invalidSelection', 'Invalid coin or network selection')
        );
        return;
      }

      // Add creator_id to the coin network data and ensure proper data types
      const coinNetworkData = {
        // Use the correct field names expected by the API
        coin_id: selectedCoinObj.id,
        network_id: selectedNetworkObj.id,
        // Keep numeric fields as strings as expected by the API validation schema
        decimal: values.decimal ? values.decimal.toString() : '0',
        min_transaction: values.min_transaction ? values.min_transaction.toString() : '0',
        max_transaction: values.max_transaction ? values.max_transaction.toString() : '0',
        min_base_coin: values.min_base_coin ? values.min_base_coin.toString() : '0',
        deposit_fee: values.deposit_fee ? values.deposit_fee.toString() : '0',
        fee: values.fee ? values.fee.toString() : '0',
        confirm_count: values.confirm_count ? values.confirm_count.toString() : '0',
        // Ensure same_address is a string with "Yes" or "No"
        same_address: values.same_address || 'Yes',
        status: values.status || 'Active',
        // Set contract_address to empty string if not provided
        contract_address: values.contract_address || '',
        // Using a numeric value for creator_id as required by the model
        creator_id: creatorId
      };

      // Log the formatted data for debugging
      console.log('Formatted coin network data for update:', coinNetworkData);
      console.log('Selected coin:', selectedCoinObj);
      console.log('Selected network:', selectedNetworkObj);

      // Submit the data
      try {
        // Add additional logging to help diagnose issues
        console.log('Submitting data to API:', JSON.stringify(coinNetworkData, null, 2));

        await updateCoinNetworkMutation.mutateAsync({
          id: selectedCoinNetwork.id,
          coinNetworkData: coinNetworkData
        });

        console.log('API call successful');
      } catch (apiError: any) {
        console.error('API Error Response:', apiError.response?.data);

        // Show detailed error message to the user
        if (apiError.response?.data?.error?.details?.errors) {
          const errorMessages = apiError.response.data.error.details.errors;
          showErrorNotification(
            t('coinNetwork.updateError', 'Failed to update coin network'),
            Array.isArray(errorMessages) ? errorMessages.join('\n') : String(errorMessages)
          );
        } else if (apiError.response?.data?.error?.message) {
          showErrorNotification(
            t('coinNetwork.updateError', 'Failed to update coin network'),
            apiError.response.data.error.message
          );
        }

        throw apiError;
      }

      // Show success notification
      showSuccessNotification(t('coinNetwork.updateSuccess', 'Coin network updated successfully'));

      // Close the modal and reset the form
      setIsEditModalVisible(false);
      form.resetFields();

      // Reset search input if exists
      if (searchText) {
        setSearchText('');
      }
    } catch (error: any) {
      console.error('Error updating coin network:', error);

      // Show more detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error.message || '';
        showErrorNotification(
          t('coinNetwork.updateError', 'Failed to update coin network'),
          errorMessage
        );
      } else {
        showErrorNotification(t('coinNetwork.updateError', 'Failed to update coin network'));
      }
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{t('coinNetwork.title', 'Coin-Network Relationships')}</Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder={t('search', 'Search coin networks')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchCoinNetworks()}
              loading={isLoadingCoinNetworks}
            >
              {t('common:actions.refresh', 'Refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={isLoadingCoins || isLoadingNetworks}
            >
              {t('coinNetwork.addRelationship', 'Add Relationship')}
            </Button>
          </Space>
        </Col>
      </Row>

      {isCoinNetworksError && (
        <Alert
          message={t('coinNetwork.errorTitle', 'Error Loading Coin Networks')}
          description={t('coinNetwork.errorDescription', 'There was an error loading the coin networks data. Please try refreshing the page.')}
          type="error"
          showIcon
          style={{ marginBottom: 16, marginTop: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        {isLoadingCoinNetworks ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Filter dropdowns */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('filterByCoin', 'Filter by Coin')}
                  style={{ width: '100%' }}
                  allowClear
                  value={filterCoin}
                  onChange={(value) => setFilterCoin(value)}
                  disabled={isLoadingCoins}
                >
                  <Option key="all-coins" value="">
                    {t('allCoins', 'All Coins')}
                  </Option>
                  {coins.map((coin: any) => (
                    <Option key={coin.id} value={coin.symbol}>
                      {coin.symbol} - {coin.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Col>
              <Col span={12}>
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('filterByNetwork', 'Filter by Network')}
                  style={{ width: '100%' }}
                  allowClear
                  value={filterNetwork}
                  onChange={(value) => setFilterNetwork(value)}
                  disabled={isLoadingNetworks}
                >
                  <Option key="all-networks" value="">
                    {t('allNetworks', 'All Networks')}
                  </Option>
                  {networks.map((network: any) => (
                    <Option key={network.id} value={network.title}>
                      {network.title} - {network.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={filteredCoinNetworks}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredCoinNetworks.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, size) => {
                  setCurrentPage(page);
                  if (size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1); // Reset to first page when page size changes
                  }
                },
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                  setCurrentPage(1); // Reset to first page when page size changes
                }
              }}
              rowKey="key"
              locale={{ emptyText: searchText || filterCoin || filterNetwork
                ? t('noNetworksFound', 'No coin networks match your filters')
                : t('noCoinNetworks', 'No coin networks found')
              }}
            />
          </>
        )}
      </Card>

      {/* Modal for adding a new coin network */}
      <Modal
        title={t('coinNetwork.addRelationship', 'Add Coin-Network Relationship')}
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        footer={null}
        width={700}
        confirmLoading={createCoinNetworkMutation.isPending}
      >
        {isLoadingCoins || isLoadingNetworks ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('loadingData', 'Loading coins and networks data...')}
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddFormSubmit}
            disabled={createCoinNetworkMutation.isPending || isCoinsError || isNetworksError}
          >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="coin"
                label={t('coin')}
                rules={[{ required: true, message: t('coinRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('selectCoin')}
                  onChange={handleCoinChange}
                  loading={isLoadingCoins}
                  disabled={isLoadingCoins || isCoinsError}
                >
                  {coins.map((coin: any) => (
                    <Option key={coin.id} value={coin.symbol}>
                      {coin.symbol} - {coin.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="network"
                label={t('network')}
                rules={[{ required: true, message: t('networkRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('selectNetwork')}
                  onChange={handleNetworkChange}
                  loading={isLoadingNetworks}
                  disabled={isLoadingNetworks || isNetworksError}
                >
                  {networks.map((network: any) => (
                    <Option key={network.id} value={network.title}>
                      {network.title} - {network.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
                {isCheckingCoinNetwork && (
                  <div style={{ marginTop: 8 }}>
                    <Spin size="small" /> {t('checkingCombination', 'Checking if this combination exists...')}
                  </div>
                )}
                {existingCoinNetwork && existingCoinNetwork.length > 0 && (
                  <div style={{ color: '#ff4d4f', marginTop: 8 }}>
                    {t('coinNetwork.combinationExists', 'This coin and network combination already exists')}
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contract_address"
                label={t('contractAddress')}
                help={t('contractAddressHelp', 'Leave empty for native tokens')}
              >
                <Input placeholder={t('contractAddressPlaceholder', 'e.g. 0x...')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="decimal"
                label={t('decimal')}
                rules={[{ required: true, message: t('decimalRequired', 'Please enter decimal places') }]}
                initialValue={18}
              >
                <InputNumber min={0} max={18} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fee"
                label={t('fee')}
                rules={[{ required: true, message: t('feeRequired', 'Please enter fee') }]}
                initialValue="0.0001"
              >
                <Input placeholder={t('feePlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="min_transaction"
                label={t('minTransaction')}
                rules={[{ required: true, message: t('minTransactionRequired', 'Please enter minimum transaction amount') }]}
                initialValue="0.001"
              >
                <Input placeholder={t('minTransactionPlaceholder', 'e.g. 0.001')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="max_transaction"
                label={t('maxTransaction')}
                rules={[{ required: true, message: t('maxTransactionRequired', 'Please enter maximum transaction amount') }]}
                initialValue="1000"
              >
                <Input placeholder={t('maxTransactionPlaceholder', 'e.g. 1000')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="min_base_coin"
                label={t('minBaseCoin')}
                rules={[{ required: true, message: t('minBaseCoinRequired', 'Please enter minimum base coin amount') }]}
                initialValue="0.0001"
              >
                <Input placeholder={t('minBaseCoinPlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deposit_fee"
                label={t('depositFee')}
                rules={[{ required: true, message: t('depositFeeRequired', 'Please enter deposit fee') }]}
                initialValue="0.0001"
              >
                <Input placeholder={t('depositFeePlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirm_count"
                label={t('confirmCount', 'Confirm Count')}
                rules={[{ required: true, message: t('confirmCountRequired', 'Please enter confirm count') }]}
                initialValue={12}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="12"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="same_address"
                label={t('sameAddress', 'Same Address')}
                initialValue="Yes"
                rules={[{ required: true, message: t('sameAddressRequired', 'Please select same address option') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..." placeholder={t('selectSameAddress', 'Select same address option')}>
                  <Option value="Yes">{t('yes', 'Yes')}</Option>
                  <Option value="No">{t('no', 'No')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('status')}
                rules={[{ required: true, message: t('statusRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..." placeholder={t('selectStatus')}>
                  <Option value="Active">{t('common:status.active', 'Active')}</Option>
                  <Option value="Inactive">{t('common:status.inactive', 'Inactive')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {t('common:actions.submit', 'Submit')}
            </Button>
            <Button onClick={() => setIsAddModalVisible(false)}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
          </Form.Item>
        </Form>
        )}
      </Modal>

      {/* Modal for editing a coin network */}
      <Modal
        title={t('editCoinNetwork', 'Edit Coin-Network Relationship')}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={700}
        confirmLoading={updateCoinNetworkMutation.isPending}
      >
        {isLoadingCoins || isLoadingNetworks ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('loadingData', 'Loading coins and networks data...')}
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditFormSubmit}
            disabled={updateCoinNetworkMutation.isPending || isCoinsError || isNetworksError}
          >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="coin"
                label={t('coin')}
                rules={[{ required: true, message: t('coinRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('selectCoin')}
                  loading={isLoadingCoins}
                  disabled={true} // Disable changing coin in edit mode
                >
                  {coins.map((coin: any) => (
                    <Option key={coin.id} value={coin.symbol}>
                      {coin.symbol} - {coin.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="network"
                label={t('network')}
                rules={[{ required: true, message: t('networkRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..."
                  placeholder={t('selectNetwork')}
                  loading={isLoadingNetworks}
                  disabled={true} // Disable changing network in edit mode
                >
                  {networks.map((network: any) => (
                    <Option key={network.id} value={network.title}>
                      {network.title} - {network.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contract_address"
                label={t('contractAddress')}
                help={t('contractAddressHelp', 'Leave empty for native tokens')}
              >
                <Input placeholder={t('contractAddressPlaceholder', 'e.g. 0x...')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="decimal"
                label={t('decimal')}
                rules={[{ required: true, message: t('decimalRequired', 'Please enter decimal places') }]}
              >
                <InputNumber min={0} max={18} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fee"
                label={t('fee')}
                rules={[{ required: true, message: t('feeRequired', 'Please enter fee') }]}
              >
                <Input placeholder={t('feePlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="min_transaction"
                label={t('minTransaction')}
                rules={[{ required: true, message: t('minTransactionRequired', 'Please enter minimum transaction amount') }]}
              >
                <Input placeholder={t('minTransactionPlaceholder', 'e.g. 0.001')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="max_transaction"
                label={t('maxTransaction')}
                rules={[{ required: true, message: t('maxTransactionRequired', 'Please enter maximum transaction amount') }]}
              >
                <Input placeholder={t('maxTransactionPlaceholder', 'e.g. 1000')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="min_base_coin"
                label={t('minBaseCoin')}
                rules={[{ required: true, message: t('minBaseCoinRequired', 'Please enter minimum base coin amount') }]}
              >
                <Input placeholder={t('minBaseCoinPlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deposit_fee"
                label={t('depositFee')}
                rules={[{ required: true, message: t('depositFeeRequired', 'Please enter deposit fee') }]}
              >
                <Input placeholder={t('depositFeePlaceholder', 'e.g. 0.0001')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirm_count"
                label={t('confirmCount', 'Confirm Count')}
                rules={[{ required: true, message: t('confirmCountRequired', 'Please enter confirm count') }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="12"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="same_address"
                label={t('sameAddress', 'Same Address')}
                initialValue="Yes"
                rules={[{ required: true, message: t('sameAddressRequired', 'Please select same address option') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..." placeholder={t('selectSameAddress', 'Select same address option')}>
                  <Option value="Yes">{t('yes', 'Yes')}</Option>
                  <Option value="No">{t('no', 'No')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('status')}
                rules={[{ required: true, message: t('statusRequired') }]}
              >
                <SearchableSelect searchPlaceholder="Type to search..." placeholder={t('selectStatus')}>
                  <Option value="Active">{t('common:status.active', 'Active')}</Option>
                  <Option value="Inactive">{t('common:status.inactive', 'Inactive')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {t('common:actions.submit', 'Submit')}
            </Button>
            <Button onClick={() => setIsEditModalVisible(false)}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
          </Form.Item>
        </Form>
        )}
      </Modal>
    </div>
  );
};

export default CoinNetworksManagementPage;
